import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SealCheckIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { useToast } from '@/context/ToastContext';
import {
  usePdfTemplateVersions,
  useFinalizePdfTemplateDraft,
  usePublishPdfTemplateVersion,
} from '@/api/rpc/hooks/communications/usePdfTemplates';
import { useUsersByIds } from '@/api/core/users/useUsersByIds';
import type { PdfTemplateVersionResponse } from '@erp/rpc';
import { T, useGT } from 'gt-react';

interface PdfTemplateVersionHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  pdfTemplateId: string;
  pinnedVersionId: string | null;
}

export function PdfTemplateVersionHistoryPanel({
  open,
  onClose,
  pdfTemplateId,
  pinnedVersionId,
}: PdfTemplateVersionHistoryPanelProps) {
  const { data, isLoading } = usePdfTemplateVersions(open ? pdfTemplateId : '');
  const versions = data?.versions ?? [];

  const creatorIds = useMemo(
    () => [...new Set(data?.versions?.map((v) => v.createdBy) ?? [])],
    [data?.versions],
  );
  const { data: users } = useUsersByIds(
    creatorIds,
    open && creatorIds.length > 0,
  );
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    users?.forEach((u) => map.set(u.id, u.name));
    return map;
  }, [users]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <T>Version history</T>
          </DialogTitle>
          <DialogDescription>
            <T>
              Every version of this PDF template. Publish a finalized version to
              make it the active version.
            </T>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-3 py-2 pr-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
              </div>
            ) : versions.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <T>No versions yet.</T>
              </div>
            ) : (
              versions.map((v) => (
                <VersionRow
                  key={v.id}
                  version={v}
                  isPinned={v.id === pinnedVersionId}
                  createdByName={userMap.get(v.createdBy)}
                  pdfTemplateId={pdfTemplateId}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function VersionRow({
  version,
  isPinned,
  createdByName,
  pdfTemplateId,
}: {
  version: PdfTemplateVersionResponse;
  isPinned: boolean;
  createdByName?: string;
  pdfTemplateId: string;
}) {
  const { showToast } = useToast();
  const gt = useGT();
  const finalizeMutation = useFinalizePdfTemplateDraft();
  const publishMutation = usePublishPdfTemplateVersion();

  const isDraft = version.status === 'draft';
  const canPublish = !isPinned;
  const busy = finalizeMutation.isPending || publishMutation.isPending;

  const handlePublish = async () => {
    try {
      let versionId = version.id;
      if (isDraft) {
        const { version: finalized } = await finalizeMutation.mutateAsync({
          versionId: version.id,
        });
        versionId = finalized.id;
      }
      await publishMutation.mutateAsync({
        pdfTemplateId,
        versionId,
      });
      showToast({
        title: gt('Published v{version}', {
          version: String(version.version),
        }),
        message: gt('This version is now the active PDF template.'),
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: gt('Publish failed'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  };

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium">
            <T context="version number label prefix">v</T>
            {version.version}
          </span>
          {isDraft ? (
            <Badge variant="secondary">
              <T>Draft</T>
            </Badge>
          ) : isPinned ? (
            <Badge>
              <SealCheckIcon className="mr-1 h-3 w-3" weight="fill" />
              <T>Published</T>
            </Badge>
          ) : (
            <Badge variant="outline">
              <T>Finalized</T>
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canPublish && (
            <Button
              size="sm"
              variant="outline"
              onClick={handlePublish}
              disabled={busy}
            >
              <SealCheckIcon className="mr-1 h-3 w-3" />
              {isDraft ? <T>Finalize & Publish</T> : <T>Publish</T>}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        {createdByName ?? version.createdBy} &middot;{' '}
        {new Date(version.createdAt).toLocaleString()}
      </div>

      {version.changelog && <p className="mt-2 text-sm">{version.changelog}</p>}
    </div>
  );
}
